import { useCallback, useState } from 'react';

// Constantes para cache
const PHOTO_CACHE_KEY = 'agent_photos_cache';
const PHOTO_CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 horas em millisegundos

// Função para gerenciar o cache de fotos
const PhotoCache = {
  // Salvar fotos no localStorage
  save: photos => {
    try {
      const cacheData = {
        photos,
        timestamp: Date.now()
      };
      localStorage.setItem(PHOTO_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Erro ao salvar cache de fotos:', error);
    }
  },

  // Carregar fotos do localStorage
  load: () => {
    try {
      const cached = localStorage.getItem(PHOTO_CACHE_KEY);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      const isExpired = Date.now() - cacheData.timestamp > PHOTO_CACHE_EXPIRY;

      if (isExpired) {
        localStorage.removeItem(PHOTO_CACHE_KEY);
        return null;
      }

      return cacheData.photos;
    } catch (error) {
      console.warn('Erro ao carregar cache de fotos:', error);
      return null;
    }
  },

  // Limpar cache
  clear: () => {
    try {
      localStorage.removeItem(PHOTO_CACHE_KEY);
    } catch (error) {
      console.warn('Erro ao limpar cache de fotos:', error);
    }
  },

  // Atualizar foto específica no cache
  updatePhoto: (agentId, photoUrl) => {
    try {
      const cached = PhotoCache.load();
      if (cached) {
        cached[agentId] = photoUrl;
        PhotoCache.save(cached);
      }
    } catch (error) {
      console.warn('Erro ao atualizar foto no cache:', error);
    }
  }
};

const usePhotoCache = () => {
  const [photos, setPhotos] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({
    fromCache: 0,
    fromApi: 0
  });

  // Carregar fotos de forma otimizada
  const loadPhotos = useCallback(
    async (agentIds, fetchPhotoFunction, defaultPhoto) => {
      if (!agentIds || agentIds.length === 0) return;

      setIsLoading(true);

      // Carregar cache existente
      const cachedPhotos = PhotoCache.load() || {};
      const newPhotos = { ...cachedPhotos };

      // Identificar agentes que não têm foto no cache
      const agentsWithoutPhoto = agentIds.filter(id => !cachedPhotos[id]);

      // Se todos os agentes já têm foto no cache, usar cache
      if (agentsWithoutPhoto.length === 0) {
        setPhotos(newPhotos);
        setIsLoading(false);
        setStats({
          fromCache: Object.keys(newPhotos).length,
          fromApi: 0
        });
        return;
      }

      // Buscar apenas fotos que não estão no cache
      const photoPromises = agentsWithoutPhoto.map(async agentId => {
        try {
          const photoUrl = await fetchPhotoFunction(agentId);
          newPhotos[agentId] = photoUrl || defaultPhoto;
        } catch (error) {
          console.error(`Erro ao carregar foto do agente ${agentId}:`, error);
          newPhotos[agentId] = defaultPhoto;
        }
      });

      await Promise.all(photoPromises);

      // Salvar no cache
      PhotoCache.save(newPhotos);
      setPhotos(newPhotos);
      setIsLoading(false);
      setStats({
        fromCache: Object.keys(cachedPhotos).length,
        fromApi: agentsWithoutPhoto.length
      });
    },
    []
  );

  // Atualizar foto específica
  const updatePhoto = useCallback((agentId, photoUrl) => {
    setPhotos(prev => ({
      ...prev,
      [agentId]: photoUrl
    }));
    PhotoCache.updatePhoto(agentId, photoUrl);
  }, []);

  // Limpar cache
  const clearCache = useCallback(() => {
    PhotoCache.clear();
    setPhotos({});
    setStats({ fromCache: 0, fromApi: 0 });
  }, []);

  // Obter foto específica
  const getPhoto = useCallback(
    (agentId, defaultPhoto) => {
      return photos[agentId] || defaultPhoto;
    },
    [photos]
  );

  return {
    photos,
    isLoading,
    stats,
    loadPhotos,
    updatePhoto,
    clearCache,
    getPhoto
  };
};

export default usePhotoCache;
